import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBookingFlowStore, SelectedService } from '../../stores/useBookingFlowStore';
import { useServiceStore } from '../../stores/useServiceStore';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import ServiceCard from '../../components/booking/ServiceCard';
import toast from 'react-hot-toast';

const StoreHomePage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const { service: selectedService, setStore, setService } = useBookingFlowStore();
  const { services, isLoading: servicesLoading, fetchServices } = useServiceStore();
  const { storeInfo, isLoading: storeLoading, fetchStoreInfo } = useStoreInfoStore();
  const { customer, isAuthenticated } = useCustomerAuth();

  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    if (!storeId) {
      toast.error(t('pages:booking.storeHome.loading'));
      return;
    }

    // 使用 zustand store 獲取服務列表（帶快取）
    fetchServices(storeId);

    // 使用 zustand store 獲取店家資訊（帶快取）
    fetchStoreInfo(storeId);
  }, [storeId, fetchServices, fetchStoreInfo, t]);

  // 當店家資訊載入完成時，更新預約流程 store
  useEffect(() => {
    if (storeInfo) {
      setStore(storeInfo.id, storeInfo.name);
    }
  }, [storeInfo, setStore]);

  // 當服務列表載入完成後，提取分類
  useEffect(() => {
    if (services.length > 0) {
      const uniqueCategories = Array.from(
        new Set(services.map((s) => s.category).filter(Boolean))
      ) as string[];
      setCategories(uniqueCategories);
    }
  }, [services]);

  const handleServiceSelect = (service: any) => {
    const selectedServiceData: SelectedService = {
      id: service.id,
      name: service.name,
      description: service.description,
      price: service.price,
      duration: service.duration,
      category: service.category,
    };

    setService(selectedServiceData);
    navigate(`/book/${storeId}/datetime`);
  };

  const filteredServices = selectedCategory === 'all'
    ? services
    : services.filter((s) => s.category === selectedCategory);

  if (storeLoading || servicesLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">{t('pages:booking.storeHome.loading')}</p>
        </div>
      </div>
    );
  }

  if (!storeInfo) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 dark:text-gray-400">{t('pages:booking.storeHome.storeNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* 封面圖片 */}
        {storeInfo.coverImage && (
          <div className="w-full h-64 rounded-lg overflow-hidden mb-6 shadow-lg">
            <img
              src={storeInfo.coverImage}
              alt={`${storeInfo.name} 封面`}
              className="w-full h-full object-cover"
              onError={(e) => {
                console.error('封面圖片載入失敗:', storeInfo.coverImage);
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* 店家資訊 */}
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-md p-8 mb-8">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center space-x-4">
              {/* 店鋪 Logo */}
              {storeInfo.logo && (
                <img
                  src={storeInfo.logo}
                  alt={`${storeInfo.name} Logo`}
                  className="w-20 h-20 rounded-full object-cover border-4 border-gray-200 dark:border-slate-700"
                  onError={(e) => {
                    console.error('Logo 載入失敗:', storeInfo.logo);
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
                {storeInfo.name}
              </h1>
            </div>

            {/* 登入/註冊按鈕 */}
            <div className="flex items-center space-x-3">
              {isAuthenticated && customer ? (
                <button
                  onClick={() => navigate(`/book/${storeId}/account`)}
                  className="flex items-center space-x-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-semibold transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{customer.name}</span>
                </button>
              ) : (
                <>
                  <button
                    onClick={() => navigate(`/book/${storeId}/login`)}
                    className="px-4 py-2 bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 border-2 border-gray-300 dark:border-slate-600 hover:border-brand-500 dark:hover:border-brand-500 rounded-lg font-semibold transition-colors"
                  >
                    {t('pages:booking.storeHome.login')}
                  </button>
                  <button
                    onClick={() => navigate(`/book/${storeId}/register`)}
                    className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    {t('pages:booking.storeHome.register')}
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-600 dark:text-gray-400">
            {storeInfo.phone && (
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>{storeInfo.phone}</span>
              </div>
            )}

            {storeInfo.email && (
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{storeInfo.email}</span>
              </div>
            )}

            {storeInfo.address && (
              <div className="flex items-center space-x-2 md:col-span-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{storeInfo.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* 服務選擇標題 */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('pages:booking.storeHome.selectService')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            {t('pages:booking.storeHome.subtitle')}
          </p>
        </div>

        {/* 分類篩選 */}
        {categories.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-full font-medium transition-colors ${
                selectedCategory === 'all'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              {t('pages:booking.storeHome.allCategories')}
            </button>

            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-brand-600 text-white'
                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* 服務卡片列表 */}
        {filteredServices && filteredServices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredServices.map((service) => (
              <ServiceCard
                key={service.id}
                id={service.id}
                name={service.name}
                description={service.description}
                price={service.price}
                duration={service.duration}
                category={service.category}
                onClick={() => handleServiceSelect(service)}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              {selectedCategory === 'all' ? t('pages:booking.storeHome.noServices') : t('pages:booking.storeHome.noCategoryServices')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreHomePage;
